/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import { useEffect, useMemo, useRef } from 'react'

import { useVirtualizer } from '@tanstack/react-virtual'
import { FiArrowDown, FiArrowUp, FiEye, FiEyeOff } from 'react-icons/fi'

import { ZOrderPill } from './components/ZOrderPill'
import { type ManagedGeofence, type SortKey, type SortState } from './types'

const ROW_HEIGHT = 44
const GRID = 'grid grid-cols-[minmax(0,1fr)_3.5rem_2.5rem] items-center gap-2 px-3'

interface GeofenceListProps {
  geofences: ManagedGeofence[]
  sort: SortState
  onSort: (key: SortKey) => void
  selectedId?: number
  onSelect: (id: number) => void
  onHover: (id: number | undefined) => void
}

const SortHeader = ({
  label,
  column,
  sort,
  onSort,
  className = '',
}: {
  label: string
  column: SortKey
  sort: SortState
  onSort: (key: SortKey) => void
  className?: string
}) => {
  const active = sort.key === column
  const Arrow = sort.ascending ? FiArrowUp : FiArrowDown
  return (
    <button
      type="button"
      onClick={() => onSort(column)}
      className={`flex items-center gap-1 font-semibold cursor-pointer hover:opacity-80 ${className}`}
      aria-sort={active ? (sort.ascending ? 'ascending' : 'descending') : 'none'}
    >
      {label}
      {active && <Arrow className="size-3" aria-hidden />}
    </button>
  )
}

/**
 * The company's geofences: name, layer and whether it is hidden. Virtualised,
 * so a company with thousands of geofences lists them all (Refresh stopped at 600). Clicking a row
 * opens it; hovering one picks it out on the map.
 */
export const GeofenceList = ({ geofences, sort, onSort, selectedId, onSelect, onHover }: GeofenceListProps) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const virtualizer = useVirtualizer({
    count: geofences.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 12,
  })

  // A deep link or a click on the map selects a row that may be scrolled away.
  const selectedIndex = useMemo(() => geofences.findIndex((g) => g.id === selectedId), [geofences, selectedId])
  useEffect(() => {
    if (selectedIndex >= 0) virtualizer.scrollToIndex(selectedIndex, { align: 'auto' })
  }, [selectedIndex, virtualizer])

  const items = virtualizer.getVirtualItems()
  // jsdom has no layout, so the virtualizer renders nothing there; show the first rows instead.
  const rows =
    items.length > 0
      ? items
      : geofences.slice(0, 30).map((_, index) => ({ index, start: index * ROW_HEIGHT, key: index }))

  return (
    <div className="flex h-full min-h-0 flex-col" role="table" aria-label="Geofences">
      <div
        className={`${GRID} sticky top-0 z-10 h-9 shrink-0 border-b border-primary bg-primary text-primary-content text-xs`}
        role="row"
      >
        <SortHeader label="Name" column="name" sort={sort} onSort={onSort} />
        <SortHeader label="Z-order" column="zorder" sort={sort} onSort={onSort} className="-ml-4" />
        <SortHeader label="Visible" column="visible" sort={sort} onSort={onSort} className="justify-center" />
      </div>
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-auto">
        <div className="relative w-full" style={{ height: virtualizer.getTotalSize() || rows.length * ROW_HEIGHT }}>
          {rows.map((row) => {
            const geofence = geofences[row.index]
            const isSelected = geofence.id === selectedId
            return (
              <div
                key={geofence.id}
                role="row"
                aria-selected={isSelected}
                tabIndex={0}
                onClick={() => onSelect(geofence.id)}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSelect(geofence.id)}
                onMouseEnter={() => onHover(geofence.id)}
                onMouseLeave={() => onHover(undefined)}
                className={`${GRID} absolute left-0 right-0 cursor-pointer border-b border-base-200 text-sm transition-colors ${
                  isSelected ? 'bg-primary/10' : 'hover:bg-base-200/60'
                }`}
                style={{ height: ROW_HEIGHT, transform: `translateY(${row.start}px)` }}
              >
                <span className="flex min-w-0 items-center gap-2" role="cell">
                  {/* <ShapeIcon type={shapeTypeOf(geofence.complexityTypeId)} className="shrink-0 text-base-content/50" /> */}
                  <span className={`truncate ${isSelected ? 'font-semibold' : ''}`} title={geofence.name}>
                    {geofence.name}
                  </span>
                </span>
                <span role="cell">
                  <ZOrderPill zorder={geofence.zorder} />
                </span>
                <span role="cell" className="flex justify-center">
                  {geofence.visible ? (
                    <FiEye className="size-4 text-base-content/70" aria-label="Visible" title="Visible" />
                  ) : (
                    <FiEyeOff className="size-4 text-base-content/50" aria-label="Hidden" title="Hidden from maps" />
                  )}
                </span>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
